import { propertiesApi, rentalAgreementsApi, rentalPaymentLogsApi, tenantsApi, type RentalAgreementDto, type RentalPaymentMonthDto, type TenantDto, type PropertyDto } from '../../services/api';
import { ApiError } from '../../services/core.api';
import { onLocaleChange, t } from '../../services/i18n';
import { toastService } from '../../services/toast';
import { confirmDialog } from '../shared/rc-confirm-dialog';
import './rc-tenant-list-view';
import { RcTenantListView } from './rc-tenant-list-view';
import { addMonths, hasCurrentAgreement } from './tenant-shared';
import type { AccordionTab, PaymentHistoryWindow, TenantAccordionState } from './tenant-types';
import { openDocumentsDrawer, openEditAgreementModal, openPaymentLogModal, openRentChangeWizard, openTenantDossierExportModal, openTenantForm, openTenantPortalInviteDialog, openTimelineDrawer } from './tenant-dialogs';
import { openPlaceDescriptionsDrawer } from './place-description-dialogs';

type TenantFilterStatus = '' | 'active' | 'ended';

type TenantEventDetail = {
  tenantId?: string;
  agreementId?: string;
  year?: number;
  month?: number;
  tab?: AccordionTab;
  name?: string;
  status?: TenantFilterStatus;
  propertyId?: string;
};

type PersistedTenantFilters = {
  status: TenantFilterStatus;
  propertyId: string;
};

const TENANT_EVENTS = [
  'tenant-add',
  'tenant-toggle',
  'tenant-edit',
  'tenant-link-portal-account',
  'tenant-invite-portal-account',
  'tenant-delete',
  'tenant-open-timeline',
  'tenant-open-welcome-page',
  'tenant-open-documents',
  'tenant-open-place-descriptions',
  'tenant-export-dossier',
  'tenant-edit-agreement',
  'tenant-rent-change',
  'tenant-payment-log',
  'tenant-payment-history-prev',
  'tenant-payment-history-next',
  'tenant-accordion-tab-change',
  'tenant-welcome-page-updated',
  'tenant-filter-change',
] as const;

const TENANT_FILTER_STORAGE_KEY = 'tenant-list-filters';

export class RcTenantList extends HTMLElement {
  private disposeLocaleChange?: () => void;
  private tenants: TenantDto[] = [];
  private properties: PropertyDto[] = [];
  private allAgreementsByTenant = new Map<string, RentalAgreementDto[]>();
  private agreementsCache = new Map<string, RentalAgreementDto[]>();
  private expandedTenantIds = new Set<string>();
  private loadingAccordionTenantIds = new Set<string>();
  private accordionErrorTenantIds = new Set<string>();
  private activeAccordionTabs = new Map<string, AccordionTab>();
  private paymentHistoryCache = new Map<string, PaymentHistoryWindow>();
  private paymentHistoryErrors = new Set<string>();
  private initialRouteHandled = false;
  private filters: { name: string; status: TenantFilterStatus; propertyId: string } = {
    name: '',
    status: '',
    propertyId: '',
  };

  async connectedCallback(): Promise<void> {
    const persistedFilters = this.readPersistedFilters();
    this.filters.status = persistedFilters.status;
    this.filters.propertyId = persistedFilters.propertyId;
    TENANT_EVENTS.forEach((eventName) => this.addEventListener(eventName, this.handleTenantEvent as EventListener));
    this.disposeLocaleChange = onLocaleChange(() => this.render());
    this.innerHTML = `<div class="loading">${t('common.loading')}</div>`;
    await this.loadData();
  }

  disconnectedCallback(): void {
    TENANT_EVENTS.forEach((eventName) => this.removeEventListener(eventName, this.handleTenantEvent as EventListener));
    this.disposeLocaleChange?.();
  }

  private async loadData(): Promise<void> {
    try {
      const [tenants, properties, agreements] = await Promise.all([
        tenantsApi.getAll(),
        propertiesApi.getAll(),
        rentalAgreementsApi.getAll(),
      ]);

      this.tenants = tenants;
      this.properties = properties;
      this.allAgreementsByTenant = new Map();
      agreements.forEach((agreement) => {
        if (!this.allAgreementsByTenant.has(agreement.tenantId)) {
          this.allAgreementsByTenant.set(agreement.tenantId, []);
        }
        this.allAgreementsByTenant.get(agreement.tenantId)!.push(agreement);
      });

      this.expandedTenantIds.forEach((tenantId) => {
        if (!this.tenants.some((tenant) => tenant.id === tenantId)) {
          this.expandedTenantIds.delete(tenantId);
        }
      });

      if (this.filters.propertyId && !this.getPropertyOptions().some((property) => property.id === this.filters.propertyId)) {
        this.filters.propertyId = '';
        this.persistFilters();
      }

      this.render();
      await this.handleInitialRouteTarget();
    } catch (error) {
      console.error('Failed to load data:', error);
      this.innerHTML = `<div class="empty-state">${t('tenantPage.loadError')}</div>`;
    }
  }

  private render(): void {
    this.innerHTML = '<rc-tenant-list-view></rc-tenant-list-view>';
    const view = this.querySelector('rc-tenant-list-view') as RcTenantListView | null;
    if (!view) return;

    const accordionStates = new Map<string, TenantAccordionState>();
    this.tenants.forEach((tenant) => {
      accordionStates.set(tenant.id, {
        agreements: this.agreementsCache.get(tenant.id) ?? [],
        loading: this.loadingAccordionTenantIds.has(tenant.id),
        error: this.accordionErrorTenantIds.has(tenant.id),
        activeTab: this.activeAccordionTabs.get(tenant.id) ?? 'info',
      });
    });

    view.tenants = this.filteredTenants;
    view.hasTenants = this.tenants.length > 0;
    view.filters = { ...this.filters };
    view.propertyOptions = this.getPropertyOptions();
    view.allAgreementsByTenant = this.allAgreementsByTenant;
    view.expandedTenantIds = new Set(this.expandedTenantIds);
    view.accordionStates = accordionStates;
    view.paymentHistoryCache = this.paymentHistoryCache;
    view.paymentHistoryErrors = this.paymentHistoryErrors;
    view.render();
  }

  private handleTenantEvent = async (event: Event): Promise<void> => {
    const customEvent = event as CustomEvent<TenantEventDetail>;
    const detail = customEvent.detail ?? {};

    switch (customEvent.type) {
      case 'tenant-add':
        openTenantForm({
          properties: this.properties,
          allAgreementsByTenant: this.allAgreementsByTenant,
          onSaved: async () => this.loadData(),
        });
        return;
      case 'tenant-accordion-tab-change':
        if (detail.tenantId && detail.tab) {
          this.activeAccordionTabs.set(detail.tenantId, detail.tab);
        }
        return;
      case 'tenant-toggle':
        if (detail.tenantId) {
          await this.toggleAccordion(detail.tenantId);
        }
        return;
      case 'tenant-edit': {
        const tenant = this.findTenant(detail.tenantId);
        if (tenant) {
          openTenantForm({
            tenant,
            properties: this.properties,
            allAgreementsByTenant: this.allAgreementsByTenant,
            onSaved: async () => this.loadData(),
          });
        }
        return;
      }
      case 'tenant-link-portal-account':
        if (detail.tenantId) {
          await this.handlePortalLink(detail.tenantId);
        }
        return;
      case 'tenant-invite-portal-account':
        if (detail.tenantId) {
          await this.handlePortalInvite(detail.tenantId);
        }
        return;
      case 'tenant-delete':
        if (detail.tenantId) {
          await this.handleDelete(detail.tenantId);
        }
        return;
      case 'tenant-open-timeline': {
        const tenant = this.findTenant(detail.tenantId);
        if (tenant) {
          openTimelineDrawer({
            tenant,
            agreements: this.allAgreementsByTenant.get(tenant.id) ?? [],
          });
        }
        return;
      }
      case 'tenant-open-welcome-page':
        if (detail.tenantId) {
          await this.openWelcomePage(detail.tenantId);
        }
        return;
      case 'tenant-open-documents': {
        const tenant = this.findTenant(detail.tenantId);
        if (tenant) {
          openDocumentsDrawer(tenant);
        }
        return;
      }
      case 'tenant-open-place-descriptions': {
        const tenant = this.findTenant(detail.tenantId);
        if (tenant) {
          await openPlaceDescriptionsDrawer(tenant, this.allAgreementsByTenant.get(tenant.id) ?? []);
        }
        return;
      }
      case 'tenant-export-dossier': {
        const tenant = this.findTenant(detail.tenantId);
        if (tenant) {
          openTenantDossierExportModal(tenant);
        }
        return;
      }
      case 'tenant-edit-agreement':
        await this.openAgreementEditor(detail);
        return;
      case 'tenant-rent-change':
        await this.openRentChange(detail);
        return;
      case 'tenant-payment-log':
        await this.openPaymentLog(detail);
        return;
      case 'tenant-payment-history-prev':
        await this.navigatePaymentHistory(detail, 'prev');
        return;
      case 'tenant-payment-history-next':
        await this.navigatePaymentHistory(detail, 'next');
        return;
      case 'tenant-welcome-page-updated':
        this.render();
        return;
      case 'tenant-filter-change':
        this.filters = {
          name: detail.name ?? '',
          status: detail.status ?? '',
          propertyId: detail.propertyId ?? '',
        };
        this.persistFilters();
        this.render();
        return;
      default:
        return;
    }
  };

  private get filteredTenants(): TenantDto[] {
    const nameQuery = this.filters.name.trim().toLocaleLowerCase();

    return this.tenants.filter((tenant) => {
      const agreements = this.allAgreementsByTenant.get(tenant.id) ?? [];

      if (nameQuery) {
        const fullName = `${tenant.firstName} ${tenant.lastName}`.toLocaleLowerCase();
        if (!fullName.includes(nameQuery)) {
          return false;
        }
      }

      if (this.filters.status === 'active' && !hasCurrentAgreement(agreements)) {
        return false;
      }

      if (this.filters.status === 'ended' && hasCurrentAgreement(agreements)) {
        return false;
      }

      if (this.filters.propertyId && !agreements.some((agreement) => agreement.propertyId === this.filters.propertyId)) {
        return false;
      }

      return true;
    });
  }

  private getPropertyOptions(): Array<{ id: string; address: string; displayName: string }> {
    const options = new Map<string, string>();

    this.properties.forEach((property) => {
      options.set(property.id, property.displayName || property.address);
    });

    this.allAgreementsByTenant.forEach((agreements) => {
      agreements.forEach((agreement) => {
        if (!options.has(agreement.propertyId) && agreement.propertyAddress) {
          options.set(agreement.propertyId, agreement.propertyAddress);
        }
      });
    });

    return Array.from(options.entries())
      .map(([id, address]) => ({ id, address, displayName: address }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }

  private readPersistedFilters(): PersistedTenantFilters {
    try {
      const raw = globalThis.localStorage?.getItem(TENANT_FILTER_STORAGE_KEY);
      if (!raw) {
        return { status: '', propertyId: '' };
      }

      const parsed = JSON.parse(raw) as Partial<PersistedTenantFilters>;
      return {
        status: parsed.status === 'active' || parsed.status === 'ended' ? parsed.status : '',
        propertyId: typeof parsed.propertyId === 'string' ? parsed.propertyId : '',
      };
    } catch {
      return { status: '', propertyId: '' };
    }
  }

  private persistFilters(): void {
    const persistedFilters: PersistedTenantFilters = {
      status: this.filters.status,
      propertyId: this.filters.propertyId,
    };

    globalThis.localStorage?.setItem(TENANT_FILTER_STORAGE_KEY, JSON.stringify(persistedFilters));
  }

  private findTenant(tenantId?: string): TenantDto | undefined {
    return this.tenants.find((tenant) => tenant.id === tenantId);
  }

  private findAgreement(tenantId?: string, agreementId?: string): RentalAgreementDto | undefined {
    if (!tenantId || !agreementId) return undefined;
    return (this.agreementsCache.get(tenantId) ?? []).find((agreement) => agreement.id === agreementId);
  }

  private async toggleAccordion(tenantId: string): Promise<void> {
    if (this.expandedTenantIds.has(tenantId)) {
      this.expandedTenantIds.delete(tenantId);
      this.render();
      return;
    }

    this.expandedTenantIds.add(tenantId);
    this.render();

    if (!this.agreementsCache.has(tenantId) && !this.loadingAccordionTenantIds.has(tenantId)) {
      await this.loadAccordionData(tenantId);
    }
  }

  private async handleInitialRouteTarget(): Promise<void> {
    if (this.initialRouteHandled) return;

    const params = new URLSearchParams(window.location.search);
    const tenantId = params.get('tenant') ?? undefined;
    const tenant = this.findTenant(tenantId);
    if (!tenantId || !tenant) {
      this.initialRouteHandled = true;
      return;
    }

    this.applyInitialRouteFilters(tenant, params.get('property'));
    this.initialRouteHandled = true;
    await this.toggleAccordion(tenantId);
    this.scrollToTenantRow(tenantId);

    if (params.get('section') === 'survey') {
      await this.focusSurveySection(tenantId, params.get('survey') ?? undefined);
    }
  }

  private applyInitialRouteFilters(tenant: TenantDto, routePropertyId: string | null): void {
    const agreements = this.allAgreementsByTenant.get(tenant.id) ?? [];
    const targetPropertyId = routePropertyId && this.getPropertyOptions().some((property) => property.id === routePropertyId)
      ? routePropertyId
      : agreements.find((agreement) => hasCurrentAgreement([agreement]))?.propertyId
        ?? agreements[0]?.propertyId
        ?? '';
    const targetHiddenByFilters = !this.filteredTenants.some((candidate) => candidate.id === tenant.id);

    if ((!routePropertyId && !targetHiddenByFilters) || !targetPropertyId) {
      return;
    }

    this.filters = {
      name: '',
      status: '',
      propertyId: targetPropertyId,
    };
    this.persistFilters();
    this.render();
  }

  private scrollToTenantRow(tenantId: string): void {
    const row = this.querySelector<HTMLElement>(`tr[data-tenant-row-id="${tenantId}"]`);
    row?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  }

  private async focusSurveySection(tenantId: string, surveyId?: string): Promise<void> {
    this.activeAccordionTabs.set(tenantId, 'surveys');
    const accordion = this.querySelector<HTMLElement>(`rc-tenant-accordion[data-tenant-id="${tenantId}"]`) as (HTMLElement & {
      highlightSurveyId?: string | null;
      loadSurveys?: () => Promise<void>;
      scrollToSurveySection?: (targetSurveyId?: string) => void;
    }) | null;

    if (!accordion) return;

    accordion.highlightSurveyId = surveyId ?? null;
    await accordion.loadSurveys?.();
    accordion.scrollToSurveySection?.(surveyId);
  }

  private async openWelcomePage(tenantId: string): Promise<void> {
    if (!this.expandedTenantIds.has(tenantId)) {
      await this.toggleAccordion(tenantId);
    }

    this.activeAccordionTabs.set(tenantId, 'welcome');

    const accordion = this.querySelector<HTMLElement>(`rc-tenant-accordion[data-tenant-id="${tenantId}"]`) as (HTMLElement & {
      scrollToWelcomePageSection?: () => void;
    }) | null;

    accordion?.scrollToWelcomePageSection?.();
  }

  private async loadAccordionData(tenantId: string): Promise<void> {
    this.loadingAccordionTenantIds.add(tenantId);
    this.accordionErrorTenantIds.delete(tenantId);
    this.render();

    try {
      const agreements = await rentalAgreementsApi.getByTenant(tenantId);
      this.agreementsCache.set(tenantId, agreements);
      this.allAgreementsByTenant.set(tenantId, agreements);
      await this.ensurePaymentHistoryLoaded(agreements);
    } catch (error) {
      console.error('Failed to load tenant accordion data:', error);
      this.accordionErrorTenantIds.add(tenantId);
    } finally {
      this.loadingAccordionTenantIds.delete(tenantId);
      this.render();
    }
  }

  private async refreshAccordion(tenantId: string): Promise<void> {
    this.invalidatePaymentHistoryForTenant(tenantId);
    this.agreementsCache.delete(tenantId);
    await this.loadAccordionData(tenantId);
  }

  private invalidatePaymentHistoryForTenant(tenantId: string): void {
    const agreements = [
      ...(this.agreementsCache.get(tenantId) ?? []),
      ...(this.allAgreementsByTenant.get(tenantId) ?? []),
    ];

    new Set(agreements.map((agreement) => agreement.id)).forEach((agreementId) => {
      this.paymentHistoryCache.delete(agreementId);
      this.paymentHistoryErrors.delete(agreementId);
    });
  }

  private async ensurePaymentHistoryLoaded(agreements: RentalAgreementDto[]): Promise<void> {
    const agreementsWithHistory = agreements.filter((agreement) => !!agreement.startDate);
    await Promise.all(agreementsWithHistory.map(async (agreement) => {
      if (this.paymentHistoryCache.has(agreement.id)) return;

      try {
        await this.loadPaymentHistoryWindow(agreement);
      } catch (error) {
        console.error('Failed to load rental payment history:', error);
        this.paymentHistoryErrors.add(agreement.id);
      }
    }));
  }

  private async loadPaymentHistoryWindow(agreement: RentalAgreementDto, endYear?: number, endMonth?: number): Promise<void> {
    const history = await rentalPaymentLogsApi.getByAgreement(agreement.id, 12, endYear, endMonth);
    const lastEntry = history[history.length - 1];
    this.paymentHistoryCache.set(agreement.id, {
      months: history,
      endYear: lastEntry?.year ?? endYear,
      endMonth: lastEntry?.month ?? endMonth,
    });
    this.paymentHistoryErrors.delete(agreement.id);
  }

  private async openAgreementEditor(detail: TenantEventDetail): Promise<void> {
    const tenant = this.findTenant(detail.tenantId);
    const agreement = this.findAgreement(detail.tenantId, detail.agreementId);
    if (!tenant || !agreement || !detail.tenantId) return;

    openEditAgreementModal({
      tenant,
      agreement,
      onSaved: async () => this.refreshAccordion(detail.tenantId!),
    });
  }

  private async openRentChange(detail: TenantEventDetail): Promise<void> {
    const tenant = this.findTenant(detail.tenantId);
    const agreement = this.findAgreement(detail.tenantId, detail.agreementId);
    if (!tenant || !agreement || !detail.tenantId) return;

    openRentChangeWizard({
      tenant,
      agreement,
      onSaved: async () => this.refreshAccordion(detail.tenantId!),
    });
  }

  private async openPaymentLog(detail: TenantEventDetail): Promise<void> {
    const agreement = this.findAgreement(detail.tenantId, detail.agreementId);
    if (!agreement || !detail.tenantId || !detail.year || !detail.month) return;

    const entry = (this.paymentHistoryCache.get(agreement.id)?.months ?? [])
      .find((item: RentalPaymentMonthDto) => item.year === detail.year && item.month === detail.month);

    openPaymentLogModal({
      agreement,
      year: detail.year,
      month: detail.month,
      entry,
      onSaved: async () => {
        const currentWindow = this.paymentHistoryCache.get(agreement.id);
        this.paymentHistoryCache.delete(agreement.id);
        await this.loadPaymentHistoryWindow(agreement, currentWindow?.endYear, currentWindow?.endMonth);
        await this.refreshAccordion(detail.tenantId!);
      },
    });
  }

  private async navigatePaymentHistory(detail: TenantEventDetail, direction: 'prev' | 'next'): Promise<void> {
    const agreement = this.findAgreement(detail.tenantId, detail.agreementId);
    if (!agreement || !detail.tenantId) return;

    const history = this.paymentHistoryCache.get(agreement.id)?.months ?? [];
    if (!history.length) return;

    const nextEnd = direction === 'prev'
      ? addMonths(history[0].year, history[0].month, -1)
      : addMonths(history[history.length - 1].year, history[history.length - 1].month, 12);

    await this.loadPaymentHistoryWindow(agreement, nextEnd.year, nextEnd.month);
    this.render();
  }

  private async handleDelete(id: string): Promise<void> {
    const confirmed = await confirmDialog(t('tenantPage.confirmDelete'));
    if (!confirmed) return;

    try {
      await tenantsApi.delete(id);
      await this.loadData();
    } catch (error) {
      console.error('Failed to delete tenant:', error);
    }
  }

  private async handlePortalLink(tenantId: string): Promise<void> {
    const tenant = this.findTenant(tenantId);
    if (!tenant) return;

    const confirmed = await confirmDialog(
      t('tenantPage.portalLinkConfirm', { name: `${tenant.firstName} ${tenant.lastName}`.trim() }),
      t('tenantPage.portalLinkAction'));
    if (!confirmed) return;

    try {
      await tenantsApi.linkPortalAccount(tenantId);
      await this.loadData();
      toastService.success(t('tenantPage.portalLinkSuccess', { name: `${tenant.firstName} ${tenant.lastName}`.trim() }));
    } catch (error) {
      console.error('Failed to link tenant portal account:', error);
      toastService.error(error instanceof ApiError ? error.message : t('tenantPage.portalLinkError'));
    }
  }

  private async handlePortalInvite(tenantId: string): Promise<void> {
    const tenant = this.findTenant(tenantId);
    if (!tenant) return;

    await openTenantPortalInviteDialog(tenant, async () => this.loadData());
  }
}

if (!customElements.get('rc-tenant-list')) {
  customElements.define('rc-tenant-list', RcTenantList);
}
